import cn from 'classnames';
import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import FormattedText from '@nespresso/catalog-components-react/containers/FormattedText';
import SavingTag, {
  type SavingTagProps
} from '@nespresso/catalog-components-react/controls/SavingTag';
import {
  getDiscountedPromoPrice,
  getPromotionPriceSaving,
  type PromotionTypes,
  type StandardizedPromotionLineItem
} from '@/domain/Promotion.ts';
import type { DiscoverPromotionsDrawerProps } from '../DiscoverPromotionsDrawer.tsx';
import styles from './PromotionLineItem.module.scss';
import type { CurrencyFormatConfig } from '@/domain/Pricing.ts';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import type { Translations } from '@/widgets/ProductList/config/index.ts';
import type { ProductDiscount } from '@/domain/Product.ts';
import { GiftingIcon } from './PromotionLineItem.icons.tsx';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter.tsx';

type PromoLineItemProps = CurrencyFormatConfig & {
  promotion: StandardizedPromotionLineItem;
  productInfo: DiscoverPromotionsDrawerProps['product'];
  separator: boolean;
};

export default function PromotionLineItem({
  promotion,
  productInfo,
  separator,
  currencyCode,
  locale
}: PromoLineItemProps) {
  const formatPrice = usePriceFormatter();
  const { t } = useTranslations<Translations>();
  const { description = '', type, modifier } = promotion;
  const savingTranslations = {
    free: t('productDetailsHero.pricing.savingLabel.free'),
    percentage: t('productDetailsHero.pricing.savingLabel.percentage')
  };

  const discount = getPromotionPriceSaving(type, modifier);

  const savingTag = getSavingTag(
    discount,
    promotion.type,
    savingTranslations,
    t
  );

  const modifiedPrice = getDiscountedPromoPrice(
    productInfo.pricing.price,
    discount
  );

  const { realPrice, realStrikethroughPrice } = getPriceContainerData(
    productInfo.pricing.price,
    modifiedPrice,
    type
  );

  return (
    <li class={cn(styles.listItem, { [styles.separator]: separator })}>
      <div class={styles.price}>
        {discount && (
          <>
            {savingTag && <SavingTag {...savingTag} />}
            <PriceContainer
              locale={locale}
              currencyCode={currencyCode}
              price={realPrice}
              strikethroughPrice={realStrikethroughPrice}
              layoutMode="column"
              layoutOrder="price-first"
              priceFormatter={(v) => formatPrice(v)}
            />
          </>
        )}
        {promotion.type === 'FREE' && savingTag && <SavingTag {...savingTag} />}
        {!discount && <span> </span>}
      </div>
      {description && (
        <div class={styles.details}>
          <FormattedText className={styles.externalHtml}>
            {description}
          </FormattedText>
        </div>
      )}
    </li>
  );
}

/**
 * Build props for the saving tag, based on the promo discount type.
 * Free promos use secondary variant style
 * Else, use default
 */
export function getSavingTag(
  discount: ProductDiscount | undefined,
  type: PromotionTypes,
  translations: {
    free: string;
    percentage: string;
  },
  interpolatorFunc: (str: string, data: object) => string
): SavingTagProps | undefined {
  switch (true) {
    case type === 'FREE': {
      return {
        text: translations.free,
        variant: 'secondary',
        Icon: GiftingIcon
      };
    }
    case undefined === discount: {
      return undefined;
    }

    case type === 'PERCENTAGE': {
      return {
        text: interpolatorFunc(translations.percentage, {
          value: discount.percentageAmount!.toString()
        })
      };
    }
    default:
      return undefined;
  }
}

/**
 * Determine the price container props for price and strikethrough price,
 * based on discounts and promo types.
 * @returns
 */
export function getPriceContainerData(
  price: number,
  discountedPrice: number | undefined,
  promotionType: PromotionTypes
): {
  realPrice: number | undefined;
  realStrikethroughPrice: number | undefined;
} {
  // strike thru price is actually the product price or not set
  const strikethroughPrice =
    discountedPrice !== undefined && price !== discountedPrice
      ? price
      : undefined;

  // If there's a discount then the strikethru price is actually the product price!
  const realStrikethroughPrice = strikethroughPrice ? price : undefined;
  // If there's a discount then the price is actually the discounted price!
  let realPrice: undefined | number = price;
  if (discountedPrice && strikethroughPrice) {
    realPrice = discountedPrice;
  }
  if (promotionType === 'FREE') {
    realPrice = undefined;
  }
  if (promotionType === 'OTHER') {
    realPrice = undefined;
  }

  return {
    realPrice,
    realStrikethroughPrice
  };
}
